import * as React from 'react'
import { createPortal } from 'react-dom'
import { ControlledMenu, MenuItem } from '@szhsin/react-menu'
import { ChartScanActivity } from '@/lib/api/company/chartScan'
import { ChartEditor, replaceTime } from './useChartEditor'
import { clockMinute, dayLength, dayOffset, inDayPieces } from './chartDay'
import { ActivityContextMenu } from './ActivityContextMenu'
import { VehicleChangeDialog } from './VehicleChangeDialog'
import { ChartCountryDialog } from './ChartCountryDialog'
import { useChartT } from '../i18n'

type Position = { x: number; y: number }
type MenuTarget = {
  activity: ChartScanActivity | null
  position: Position
  minute?: number
}
type Dialog =
  | { kind: 'begin' }
  | { kind: 'end' }
  | { kind: 'border'; atIso: string }
  // `first` edits the day's first vehicle; `editId` an existing change; neither adds one.
  | { kind: 'vehicle'; atIso: string; editId?: number; first?: boolean }

export function useDiscContextMenu(
  companyId: string,
  discId: number,
  editor: ChartEditor,
) {
  const T = useChartT()
  const [menu, setMenu] = React.useState<MenuTarget | null>(null)
  const [markMenu, setMarkMenu] = React.useState<{ id: number; position: Position } | null>(null)
  const [dialog, setDialog] = React.useState<Dialog | null>(null)
  React.useEffect(() => {
    setMenu(null)
    setMarkMenu(null)
    setDialog(null)
  }, [discId])
  React.useEffect(() => {
    if (!dialog) return undefined
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDialog(null)
    }
    window.addEventListener('keydown', escape)
    return () => window.removeEventListener('keydown', escape)
  }, [dialog])

  const pieces = inDayPieces(editor.activities, editor.dayStartDate, editor.dayEndDate)
  const offsetOf = (iso: string) => dayOffset(clockMinute(iso), clockMinute(editor.dayStartDate))

  // WPF Alt+N: change the vehicle on the selected activity, or on the last one of the day.
  // Where a vehicle already starts it edits that one, like the menu.
  const openForSelection = React.useRef(() => {})
  openForSelection.current = () => {
    if (menu || markMenu || dialog) return
    const activity =
      editor.activities.find((a) => editor.selectedActivityIds.has(a.activityId)) ??
      pieces[pieces.length - 1]?.activity
    if (!activity) return
    const existing = editor.additionalVehiclesUsed.find((v) => v.startTime === activity.startTime)
    if (existing) setDialog({ kind: 'vehicle', atIso: existing.startTime, editId: existing.vehiclesUsedId })
    else if (pieces[0]?.activity.activityId === activity.activityId)
      setDialog({ kind: 'vehicle', atIso: editor.dayStartDate, first: true })
    else setDialog({ kind: 'vehicle', atIso: activity.startTime })
  }
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // AltGr arrives as Ctrl+Alt on Windows and types a letter.
      if (!e.altKey || e.ctrlKey || e.metaKey || e.code !== 'KeyN') return
      const el = e.target as HTMLElement | null
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return
      e.preventDefault()
      openForSelection.current()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const open = (
    activity: ChartScanActivity | null,
    position: Position,
    minute?: number,
  ) => {
    if (activity && !editor.selectedActivityIds.has(activity.activityId))
      editor.setSelected([activity.activityId])
    setMenu({ activity, position, minute })
  }
  const target = menu?.activity ?? null
  const vehicle = editor.additionalVehiclesUsed.find(
    (v) => v.startTime === target?.startTime,
  )
  // WPF keeps the first vehicle on the day's first activity; there it can only be edited.
  const isFirstActivity = target != null && pieces[0]?.activity.activityId === target.activityId
  const fromDisc = menu?.minute != null
  const editingVehicle =
    dialog?.kind === 'vehicle'
      ? editor.additionalVehiclesUsed.find((v) => v.vehiclesUsedId === dialog.editId)
      : undefined
  // WPF RefreshOdometerValues: a vehicle used earlier on this chart continues from where it ended.
  const lastOdometerOf = (vehicleId: number, time: string) => {
    const at = offsetOf(replaceTime(editor.dayStartDate, time))
    const earlier = [
      { vehicleId: editor.vehicleId, offset: 0, odometerEnd: editor.firstVehicleOdometerEnd },
      ...editor.additionalVehiclesUsed
        .filter((v) => v !== editingVehicle)
        .map((v) => ({ vehicleId: v.vehicleId, offset: offsetOf(v.startTime), odometerEnd: v.odometerEnd })),
    ]
      .filter((v) => v.vehicleId === vehicleId && v.offset < at)
      .sort((a, b) => a.offset - b.offset)
      .pop()
    return earlier ? earlier.odometerEnd : null
  }
  const editVehicleChange = (id: number) => {
    const v = editor.additionalVehiclesUsed.find((x) => x.vehiclesUsedId === id)
    if (v) setDialog({ kind: 'vehicle', atIso: v.startTime, editId: id })
  }
  // Dragging the marker on the disc (WPF TachographChartVehicleChangeControl).
  const moveVehicleChange = (id: number, minute: number) => {
    const v = editor.additionalVehiclesUsed.find((x) => x.vehiclesUsedId === id)
    if (!v) return
    const atIso = replaceTime(v.startTime, minuteToTime(minute))
    const offset = offsetOf(atIso)
    if (offset <= 0 || offset >= dayLength(editor.dayStartDate, editor.dayEndDate)) return
    editor.editVehicleChange(id, atIso, v.vehicleId ?? null, v.odometerStart, v.odometerEnd)
  }
  const startTime =
    menu?.minute != null
      ? minuteToTime(menu.minute)
      : target?.startTime.slice(11, 16)
  const endTime =
    menu?.minute != null
      ? minuteToTime(menu.minute)
      : target?.endTime.slice(11, 16)
  const itemClass = 'tw-my-0 tw-px-4 tw-py-1 tw-text-sm tw-text-content-base'
  const content = (
    <>
      <ActivityContextMenu
        position={menu?.position ?? null}
        target={target}
        onClose={() => setMenu(null)}
        selectionCount={target ? editor.selectedActivityIds.size : 0}
        onRemove={editor.deleteSelected}
        onAdd={editor.addActivity}
        onChangeType={editor.changeSelectedType}
        onToggleFerry={editor.toggleSelectedFerry}
        onDeleteAllOfType={editor.deleteAllOfType}
        onSetDayStart={
          startTime ? () => editor.setDayStartTime(startTime) : undefined
        }
        onSetDayEnd={endTime ? () => editor.setDayEndTime(endTime) : undefined}
        onSetBeginCountry={() => setDialog({ kind: 'begin' })}
        onSetEndCountry={() => setDialog({ kind: 'end' })}
        onVehicleChange={
          // WPF: the disc always adds on the minute under the cursor; the list adds only
          // where no vehicle starts yet.
          target && (fromDisc || !(vehicle || isFirstActivity))
            ? () => {
                const atIso =
                  menu?.minute != null
                    ? replaceTime(target.startTime, minuteToTime(menu.minute))
                    : target.startTime
                setDialog({ kind: 'vehicle', atIso })
              }
            : undefined
        }
        onEditVehicleChange={
          vehicle
            ? () => editVehicleChange(vehicle.vehiclesUsedId)
            : isFirstActivity
              ? () => setDialog({ kind: 'vehicle', atIso: editor.dayStartDate, first: true })
              : undefined
        }
        onRemoveVehicleChange={
          vehicle
            ? () => editor.removeVehicleChange(vehicle.vehiclesUsedId)
            : undefined
        }
        onCountryChange={() => {
          if (target) setDialog({ kind: 'border', atIso: target.startTime })
        }}
      />
      {markMenu && (
        <ControlledMenu
          anchorPoint={markMenu.position}
          state="open"
          onClose={() => setMarkMenu(null)}
          portal
          className="tw-z-[10000]"
          onContextMenu={(e) => e.preventDefault()}
        >
          <MenuItem className={itemClass} onClick={() => editVehicleChange(markMenu.id)}>
            {T('editVehicleChange', 'Edytuj zmianę pojazdu')}
          </MenuItem>
          <MenuItem className={itemClass} onClick={() => editor.removeVehicleChange(markMenu.id)}>
            {T('removeVehicleChange', 'Usuń zmianę pojazdu')}
          </MenuItem>
        </ControlledMenu>
      )}
      {dialog &&
        createPortal(
          dialog.kind === 'vehicle' ? (
            <VehicleChangeDialog
              companyId={companyId}
              atIso={dialog.atIso}
              timeLocked={dialog.first}
              initial={
                dialog.first
                  ? {
                      vehicleId: editor.vehicleId,
                      odometerStart: editor.firstVehicleOdometerStart,
                      odometerEnd: editor.firstVehicleOdometerEnd,
                    }
                  : editingVehicle
              }
              dayStartIso={editor.dayStartDate}
              dayEndIso={editor.dayEndDate}
              lastOdometerOf={lastOdometerOf}
              chartDistance={(time) =>
                dialog.first
                  ? editor.distanceFromChart
                  : editor.distanceForChangeAt(replaceTime(dialog.atIso, time), dialog.editId)
              }
              onConfirm={(values) => {
                const atIso = replaceTime(dialog.atIso, values.time)
                if (dialog.first)
                  editor.setFirstVehicle(values.vehicleId, values.odometerStart, values.odometerEnd, true)
                else if (editingVehicle)
                  editor.editVehicleChange(
                    editingVehicle.vehiclesUsedId,
                    atIso,
                    values.vehicleId,
                    values.odometerStart,
                    values.odometerEnd,
                  )
                else
                  editor.addVehicleChange(
                    atIso,
                    values.vehicleId,
                    values.odometerStart,
                    values.odometerEnd,
                  )
                setDialog(null)
              }}
              onCancel={() => setDialog(null)}
            />
          ) : (
            <ChartCountryDialog
              companyId={companyId}
              title={
                dialog.kind === 'begin'
                  ? T('setBeginCountry', 'Ustaw kraj początku dnia')
                  : dialog.kind === 'end'
                    ? T('setEndCountry', 'Ustaw kraj końca dnia')
                    : T('insertBorderCrossing', 'Wstaw przekroczenie granicy')
              }
              value={
                dialog.kind === 'begin'
                  ? editor.beginCountry
                  : dialog.kind === 'end'
                    ? editor.endCountry
                    : null
              }
              onConfirm={(country) => {
                if (dialog.kind === 'begin')
                  editor.setBeginCountry(country.nationId)
                else if (dialog.kind === 'end')
                  editor.setEndCountry(country.nationId)
                else editor.addCountryChange(dialog.atIso, country.code)
                setDialog(null)
              }}
              onCancel={() => setDialog(null)}
            />
          ),
          document.body,
        )}
    </>
  )
  return {
    open,
    content,
    isInteracting: menu != null || markMenu != null || dialog != null,
    openVehicleChangeMenu: (id: number, position: Position) => setMarkMenu({ id, position }),
    moveVehicleChange,
  }
}

function minuteToTime(minute: number) {
  const value = ((Math.round(minute) % 1440) + 1440) % 1440
  return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`
}
